import { FormLayout, Input, RadioGroup, Select, SubmitButton } from '@panwds/react-form';
import { Button, Sheet, SheetBody, Link, Banner } from "@panwds/react-ui";
import { useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslate } from "../../../../../customHooks";
import { isIpOrFqdn, validateResourceConfigArn, validateUnique } from '../../../../../utils/validate';
import { FormattedMessage } from "react-intl";
import { getResourceTypeOptions, DOCUMENTATION_URLS } from './constants';
import { PrivateAccessCreateProps, PrivateAccessConfig } from './types';

export const PrivateAccessCreate = (props: PrivateAccessCreateProps) => {
    const { close, createPrivateAccess, currentPrivateAccess, disabled = false } = props;
    const translate = useTranslate();

    const formMethods = useForm<PrivateAccessConfig>({
        mode: 'onBlur',
        defaultValues: {
            ResourceConfigType: 'Single'
        }
    });
    const {
        formState: { isDirty, isValid, }
    } = formMethods

    const resourceConfigType = formMethods.watch('ResourceConfigType');
    const selectedType = formMethods.watch('Type');

    const onSubmit = useCallback(
        async (data: PrivateAccessConfig) => {
            createPrivateAccess(data);
        }, [createPrivateAccess]
    );

    return (
        <>
         <Sheet
            onClose={() => close()}
            title={translate(`generic.addNew`)}
            isOpen
            showMask={true}
            position="md"
        >
            <SheetBody>
                <div className="tw-relative">
                    <div className="tw-mb-4">
                        <Banner
                            type="inline"
                            appearance="standard"
                            showIcon>
                            {translate('resources.firewallsV2.privateAccess.prerequisitesReminder')}
                        </Banner>
                    </div>
                    <FormProvider {...formMethods}>
                        <form onSubmit={formMethods.handleSubmit(onSubmit)}>
                            <FormLayout>
                                    <Input
                                        label={translate('generic.name')}
                                        name="Name"
                                        dataMetrics="cloudngfw-firewall-private=-access-create-name"
                                        placeholder={translate('resources.firewallsV2.privateAccess.customName')}
                                        requiredAsterisk
                                        disabled={disabled}
                                        register={{ validate: validateUnique(
                                            currentPrivateAccess?.Firewall?.PvtAccess || [],
                                            'Name',
                                            (value) => `The name "${value}" already exists. Please choose a different name.`,
                                            true // case-sensitive
                                        ) }}
                                    />
                                    <Select
                                        items={getResourceTypeOptions(translate)}
                                        label={translate(`resources.firewallsV2.privateAccess.resourceType`)}
                                        name="Type"
                                        dataMetrics="cloudngfw-firewall-private=-access-create-resource-type"
                                        placeholder={translate(`resources.firewallsV2.privateAccess.selectResourceType`)}
                                        simpleFormat
                                        menuStyle={{ width: '15rem' }}
                                        requiredAsterisk
                                        disabled={disabled}
                                    />
                                    <div>
                                        <Input
                                            label={translate('resources.firewallsV2.privateAccess.host')}
                                            name="Host"
                                            dataMetrics="cloudngfw-firewall-private=-access-create-agent-ip"
                                            requiredAsterisk
                                            disabled={disabled}
                                            register={{validate: {
                                                isIpOrFqdn: isIpOrFqdn,
                                                isUnique: validateUnique(
                                                    currentPrivateAccess?.Firewall?.PvtAccess || [],
                                                    (item, value) => item.Type === selectedType && item.Host === value,
                                                    (value) => `The host "${value}" is already configured for this type.`,
                                                )
                                            }}}
                                        />
                                        <div className="tw-text-xs tw-text-gray-600 tw-mt-1">
                                            <FormattedMessage
                                                id="resources.firewallsV2.privateAccess.hostHelpText"
                                                values={{
                                                    document: (
                                                        <Link external href={DOCUMENTATION_URLS.USER_ID_AGENT} target="_blank" rel="noopener noreferrer">
                                                            document
                                                        </Link>
                                                    )
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <RadioGroup
                                        horizontal
                                        label={translate('resources.firewallsV2.privateAccess.resourceConfigType')}
                                        name="ResourceConfigType"
                                        dataMetrics="cloudngfw-firewall-private-access-create-resource-config-type"
                                        requiredAsterisk
                                        disabled={disabled}
                                        items={[{
                                            value: 'Single',
                                            children: translate('resources.firewallsV2.privateAccess.resourceConfigTypeSingle'),
                                        }, {
                                            value: 'Group',
                                            children: translate('resources.firewallsV2.privateAccess.resourceConfigTypeGroup'),
                                        }]}
                                    />
                                    <div>
                                        <Input
                                            name="ResourceArn"
                                            label={translate('resources.firewallsV2.privateAccess.AWSResourceId')}
                                            dataMetrics="cloudngfw-firewall-private-access-create-aws-resource-id"
                                            requiredAsterisk
                                            disabled={disabled}
                                            register={{validate: validateResourceConfigArn}}
                                        />
                                        <div className="tw-text-xs tw-text-gray-600 tw-mt-1">
                                            <FormattedMessage
                                                id="resources.firewallsV2.privateAccess.AWSResourceIdHelpText"
                                                values={{
                                                    document: (
                                                        <Link external href={DOCUMENTATION_URLS.RESOURCE_GATEWAY} target="_blank" rel="noopener noreferrer">
                                                            document
                                                        </Link>
                                                    )
                                                }}
                                            />
                                        </div>
                                    </div>

                                    {resourceConfigType === 'Group' && (
                                        <div>
                                            <Input
                                                name="ChildResourceArn"
                                                label={translate('resources.firewallsV2.privateAccess.childResourceARN')}
                                                dataMetrics="cloudngfw-firewall-private-access-create-child-resource-arn"
                                                requiredAsterisk
                                                disabled={disabled}
                                                register={{validate: validateResourceConfigArn}}
                                            />
                                            <div className="tw-text-xs tw-text-gray-600 tw-mt-1">
                                                <FormattedMessage
                                                    id="resources.firewallsV2.privateAccess.childResourceARNHelpText"
                                                    values={{
                                                        document: (
                                                            <Link external href={DOCUMENTATION_URLS.RESOURCE_GATEWAY} target="_blank" rel="noopener noreferrer">
                                                                document
                                                            </Link>
                                                        )
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )}


                                    <div className="tw-flex tw-justify-end">
                                        <Button addClassName='tw-mr-2' appearance={"secondary"} onClick={() => close()}>
                                            {translate("generic.cancel")}
                                        </Button>
                                        <SubmitButton disabled={!isValid || !isDirty || disabled}>{translate(`generic.add`)}</SubmitButton>
                                    </div>
                            </FormLayout>
                        </form>
                    </FormProvider>
                </div>
            </SheetBody>
        </Sheet>
    </>
    );
};

PrivateAccessCreate.displayName = 'PrivateAccessCreate';
